import React, { Fragment } from 'react';

import { Grid, Link, Paper, Typography, useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { DateTime } from 'luxon';
import ZendeskLink from 'src/components/Zendesk/ZendeskLink';
import ConfigContext from 'src/context/config';
import useNonNullContext from 'src/hooks/useNonNullContext';

interface Props {
  // prop used to determine if the cart sticky footer is rendering, to add some margin at the bottom so the <FunnelFooter> is not blocked
  isShowingCart: boolean;
}
const FunnelFooter: React.FC<Props> = ({ isShowingCart }) => {
  const configContext = useNonNullContext(ConfigContext);

  const theme = useTheme();

  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const backgroundColor = theme.palette.grey[900];
  const textColor = theme.palette.grey[300];
  const accentTextColor = theme.palette.common.white;
  const watermarkColor = theme.palette.grey[600];

  // fall back to undefined for backwards compatibility
  const { termsOfUse, privacyPolicy } = configContext.globalLinks ?? {
    termsOfUse: undefined,
    privacyPolicy: undefined,
  };

  return (
    <Paper
      css={{
        backgroundColor,
        color: textColor,
      }}
      square
    >
      {isSmallScreen ? (
        <Grid
          container
          direction="column"
          alignItems="center"
          css={{
            padding: '2rem 1rem',
            margin: 'auto',
            marginBottom: isShowingCart ? '3.5rem' : undefined,
            ['.watermark']: {
              padding: '2rem 0',
            },
          }}
        >
          <Grid marginBottom={'0.5rem'}>
            <Typography>Need more information?</Typography>
          </Grid>
          <Grid marginBottom={'0.5rem'}>
            <ZendeskLink textColor={theme.palette.grey[300]} />
          </Grid>
          <Grid marginBottom={'0.5rem'}>
            <Typography variant="subtitle1" color={watermarkColor}>
              {configContext.footer.watermark || (
                <Fragment>&copy; {DateTime.now().year}</Fragment>
              )}
            </Typography>
          </Grid>
          <Grid container direction="row" justifyContent="center">
            <Link
              href={termsOfUse.url}
              target={termsOfUse.target}
              underline="hover"
              css={{ marginRight: '0.5rem' }}
            >
              <Typography variant="body2" color={textColor}>
                {termsOfUse.displayText}
              </Typography>
            </Link>
            <Link
              href={privacyPolicy.url}
              target={privacyPolicy.target}
              underline="hover"
            >
              <Typography variant="body2" color={textColor}>
                {privacyPolicy.displayText}
              </Typography>
            </Link>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          direction="column"
          css={{
            maxWidth: theme.breakpoints.values.contentMaxWidth,
            padding: '2rem 1rem',
            margin: 'auto',
            marginBottom: isShowingCart ? '4rem' : undefined,
            ['.watermark']: {
              padding: '2rem 0',
            },
          }}
        >
          <Grid>
            <Grid container direction="row" justifyContent="space-between">
              <Grid>
                <Grid container direction="column" alignItems="baseline">
                  <Typography variant="subtitle1" color={accentTextColor}>
                    Need more information?
                  </Typography>
                  <Grid
                    css={{
                      padding: '0.25rem 0',
                    }}
                  >
                    <ZendeskLink textColor={theme.palette.grey[300]} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid>
                <Grid container direction="column" alignItems="flex-end">
                  <Typography variant="subtitle1" color={watermarkColor}>
                    {configContext.footer.watermark || (
                      <Fragment>&copy; {DateTime.now().year}</Fragment>
                    )}
                  </Typography>
                  <Grid
                    css={{
                      padding: '0.25rem 0',
                      display: 'flex',
                      alignItems: 'baseline',
                    }}
                  >
                    {termsOfUse && (
                      <Link
                        href={termsOfUse.url}
                        target={termsOfUse.target}
                        underline="hover"
                        css={{ marginRight: '0.5rem' }}
                      >
                        <Typography variant="body2" color={textColor}>
                          {termsOfUse.displayText}
                        </Typography>
                      </Link>
                    )}
                    {privacyPolicy && (
                      <Link
                        href={privacyPolicy.url}
                        target={privacyPolicy.target}
                        underline="hover"
                      >
                        <Typography variant="body2" color={textColor}>
                          {privacyPolicy.displayText}
                        </Typography>
                      </Link>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default FunnelFooter;
